import React from 'react';
import { formatDateTime, formatDuration, formatSyncType } from '../../utils/formatUtils';

// ── Helpers ──────────────────────────────────────────────────────────────────

const getTypeIcon = (type) => {
  if (!type) return 'bi-question-circle';
  if (type.includes('course')) return 'bi-book';
  if (type.includes('chapter')) return 'bi-list-nested';
  if (type.includes('content')) return 'bi-file-earmark-text';
  if (type.includes('mentor')) return 'bi-person';
  if (type.includes('attempts')) return 'bi-clipboard-check';
  if (type.includes('batch')) return 'bi-people';
  if (type.includes('users')) return 'bi-people-fill';
  return 'bi-arrow-repeat';
};

const calcProgressPct = (progress) => {
  if (!progress || !progress.total) return 0;
  return Math.min(100, Math.round((progress.processed / progress.total) * 100));
};

// ── RunningSyncCard ───────────────────────────────────────────────────────────

const RunningSyncCard = ({ sync }) => {
  const { id, type, progress = {}, logs = [], startTime } = sync;

  const progressPct = calcProgressPct(progress);
  const typeIcon = getTypeIcon(type);
  const isUserSync = type && (type.includes('users') || type === 'users-sync');
  const currentStudent = progress.currentStudent;
  const recentLogs = Array.isArray(logs) ? logs.slice(-5).reverse() : [];

  return (
    <div className="live-sync-card theme-card">
      {/* Header */}
      <div className="live-sync-header">
        <div className="live-sync-title-group">
          <div className="live-sync-type-row">
            <i className={`bi ${typeIcon} live-sync-type-icon`}></i>
            <span className="live-sync-type-label">{formatSyncType(type)}</span>
          </div>
          <div className="live-sync-id">{id}</div>
        </div>

        <span className="live-sync-elapsed">
          Running for {formatDuration(sync)}
        </span>
      </div>

      {/* Progress bar */}
      <div className="live-sync-progress-label-row">
        <span>Progress</span>
        <span className="live-sync-progress-pct">{progressPct}%</span>
      </div>
      <div className="live-sync-progress-track">
        <div
          className="live-sync-progress-fill"
          style={{ width: `${progressPct}%` }}
        ></div>
      </div>

      {/* Stats */}
      <div className="live-sync-stats">
        <div className="live-sync-stat">
          <span className="live-sync-stat-label">Processed</span>
          <span className="live-sync-stat-value muted">
            {progress.processed ?? 0} / {progress.total || '?'}
          </span>
        </div>

        {isUserSync && currentStudent > 0 && (
          <div className="live-sync-stat">
            <span className="live-sync-stat-label">Current Student</span>
            <span className="live-sync-stat-value blue">{currentStudent}</span>
          </div>
        )}

        <div className="live-sync-stat">
          <span className="live-sync-stat-label">Created</span>
          <span className="live-sync-stat-value green">{progress.created ?? 0}</span>
        </div>

        <div className="live-sync-stat">
          <span className="live-sync-stat-label">Updated</span>
          <span className="live-sync-stat-value blue">{progress.updated ?? 0}</span>
        </div>

        <div className="live-sync-stat">
          <span className="live-sync-stat-label">Failed</span>
          <span className="live-sync-stat-value red">{progress.failed ?? 0}</span>
        </div>

        {startTime && (
          <div className="live-sync-stat">
            <span className="live-sync-stat-label">Started</span>
            <span className="live-sync-stat-value muted" style={{ fontSize: '0.875rem' }}>
              {formatDateTime(startTime)}
            </span>
          </div>
        )}
      </div>

      {/* Activity log */}
      <div className="live-sync-log-section">
        <div className="live-sync-log-title">
          <i className="bi bi-activity" style={{ marginRight: '5px' }}></i>
          Recent Activity
        </div>

        <div className="live-sync-log">
          {recentLogs.length === 0 ? (
            <span className="live-sync-log-empty">No recent activity</span>
          ) : (
            recentLogs.map((entry, idx) => (
              <div key={idx} className="live-sync-log-entry">
                <span className="live-sync-log-time">
                  {entry.time ? formatDateTime(entry.time) : '—'}
                </span>
                <span className="live-sync-log-msg">{entry.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── LiveMonitor ───────────────────────────────────────────────────────────────

const LiveMonitor = ({ runningSyncs = [], isConnected = false }) => {
  return (
    <div>
      {/* Header */}
      <div className="live-monitor-header">
        <h2 className="live-monitor-title">
          <i className="bi bi-activity" style={{ color: 'var(--accent-green)', marginRight: '6px' }}></i>
          Live Monitor
        </h2>

        <span className={`live-monitor-conn-badge ${isConnected ? 'connected' : 'disconnected'}`}>
          <span className="live-monitor-conn-dot"></span>
          {isConnected ? 'Connected' : 'Disconnected'}
        </span>

        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          {runningSyncs.length} active {runningSyncs.length === 1 ? 'sync' : 'syncs'}
        </span>
      </div>

      {/* Content */}
      {runningSyncs.length === 0 ? (
        <div className="live-monitor-empty">
          <i className="bi bi-check-circle live-monitor-empty-icon" style={{ color: 'var(--accent-green)' }}></i>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '2px', color: 'var(--text-secondary)' }}>
              All clear
            </div>
            <div>No syncs are currently running. New syncs will appear here automatically.</div>
          </div>
        </div>
      ) : (
        <div className="live-monitor-syncs-grid">
          {runningSyncs.map((sync) => (
            <RunningSyncCard key={sync.id} sync={sync} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveMonitor;
